import React, { useState, useEffect, useRef } from "react";
import { Send, Search, ChevronRight, X, ChevronLeft } from "lucide-react";
import { io } from "socket.io-client";

const initialBotMessages = [{ text: "Hello!", type: "bot" }];

const VirtualAgent = () => {
  const [currentView, setCurrentView] = useState("home");
  const [messages, setMessages] = useState(initialBotMessages);
  const [input, setInput] = useState("");

  // New state to track if an option is selected to enable the chat
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Load session from local storage or create a new one
  const [sessionId, setSessionId] = useState(() => {
    const savedSessionId = localStorage.getItem("chat_session_id");
    if (savedSessionId) {
      return savedSessionId;
    }
    const newSessionId = crypto.randomUUID();
    localStorage.setItem("chat_session_id", newSessionId);
    return newSessionId;
  });

  const userEmail = localStorage.getItem("email");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!userEmail) return;

    const fetchMessages = async () => {
      try {
        // Updated fetching API to only use sessionId at the end
        const response = await fetch(
          `https://api.confidanto.com/chatbox-api/api/messages/${sessionId}`,
        );
        const data = await response.json();

        const formattedMessages = data.map((msg) => ({
          text: msg.text,
          type: msg.from === "admin" ? "bot" : "user",
        }));

        setMessages([...initialBotMessages, ...formattedMessages]);
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    };

    fetchMessages();

    const socket = io("https://api.confidanto.com");

    socket.on("newAdminMessage", (msg) => {
      if (msg.session_id === sessionId) {
        setMessages((prevMessages) => [
          ...prevMessages,
          { text: msg.text, type: "bot" },
        ]);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [userEmail, sessionId]);

  useEffect(() => {
    if (currentView === "chat") {
      scrollToBottom();
    }
  }, [messages, currentView]);

  const handleSend = () => {
    const text = input;
    if (!text.trim() || !userEmail) return;

    const newMessage = { type: "user", text };
    setMessages((prevMessages) => [...prevMessages, newMessage]);
    setInput("");

    fetch("https://api.confidanto.com/chatbox-api/api/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: userEmail,
        text: text,
        session_id: sessionId,
        // For future development: You can easily pass the selectedCategory here
        // category: selectedCategory
      }),
    })
      .then((response) => response.json())
      .catch((error) => {
        console.error("Error sending message:", error);
      });
  };

  const handleNewSession = () => {
    // Generate new ID, update state, and save to local storage
    const newSessionId = crypto.randomUUID();
    setSessionId(newSessionId);
    localStorage.setItem("chat_session_id", newSessionId);
    setMessages(initialBotMessages);
  };

  // Handler for option selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    // You can also call handleNewSession() here if selecting an option
    // should ALWAYS start a completely fresh chat session.
  };

  if (currentView === "home") {
    return (
      <div className="flex flex-col h-full w-full bg-gray-50 relative rounded-xl shadow-2xl overflow-hidden font-sans border border-gray-200 mx-auto">
        <div className="bg-[#283593] text-white pt-6 pb-16 px-6 rounded-b-[2rem] shadow-md">
          <div className=" flex justify-between items-start mb-8 ">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2"></div>
              <X
                size={20}
                className="cursor-pointer opacity-80 hover:opacity-100 transition"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-1">Hi there 👋</h1>
          <h2 className="text-3xl font-bold">How can we help?</h2>
        </div>

        <div className="flex-1 px-5 -mt-10 z-10 overflow-y-auto pb-6 scrollbar-hide">
          <button
            onClick={() => setCurrentView("chat")}
            className="w-full bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex justify-between items-center mb-5 text-left hover:shadow-md transition group"
          >
            <div>
              <h3 className="font-semibold text-gray-800 text-base">
                Send us a message
              </h3>
              <p className="text-sm text-gray-400 mt-0.5">
                We typically reply in a few hours
              </p>
            </div>
            <Send
              size={20}
              className="text-[#283593] group-hover:translate-x-1 transition-transform"
            />
          </button>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2">
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl mb-1 cursor-text">
              <span className="text-sm font-medium text-gray-700 flex-1">
                Search for help
              </span>
              <Search size={18} className="text-[#283593]" />
            </div>
            <div className="flex flex-col mt-2">
              {[
                "Connect Google Ads - User Guide",
                "Reporting Schedule",
                "Tasks - User Guide",
                "Alerts - Slack Integration",
              ].map((text, idx) => (
                <button
                  key={idx}
                  className="flex justify-between items-center w-full p-3 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition text-left"
                >
                  <span className="truncate pr-2">{text}</span>
                  <ChevronRight
                    size={16}
                    className="text-[#283593] flex-shrink-0"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const options = [
    "Billing Questions",
    "Technical Support",
    "Sales Assistance",
  ];

  return (
    <div className="flex flex-col h-full w-full bg-white relative rounded-xl shadow-2xl overflow-hidden font-sans border border-gray-200 mx-auto">
      <div className="p-4 bg-[#283593] text-white font-semibold shadow-sm z-10 flex items-center gap-3">
        <button
          onClick={() => setCurrentView("home")}
          className="hover:bg-white/20 p-1 rounded-md transition"
        >
          <ChevronLeft size={22} />
        </button>
        <span className="flex-1">Virtual Agent</span>
      </div>

      <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
        <div className="flex flex-col items-center mb-4 mt-2">
          <img
            src="https://www.w3schools.com/w3images/avatar2.png"
            alt="Agent"
            className="w-16 h-16 rounded-full shadow-sm border border-gray-200"
          />
          <p className="mt-3 font-medium text-gray-800">Hello!</p>
          <p className="text-xs text-gray-500 mb-2">
            {userEmail
              ? `You are chatting as ${userEmail}`
              : "Please log in to chat."}
          </p>
        </div>

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm shadow-sm ${
              msg.type === "bot"
                ? "bg-gray-100 text-gray-800 self-start rounded-tl-sm"
                : "bg-[#283593] text-white self-end rounded-tr-sm"
            }`}
          >
            {msg.text}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="mt-auto flex flex-col border-t border-gray-100 p-4 bg-gray-50 z-20">
        <div className="flex flex-wrap justify-center gap-2 mb-3">
          {options.map((option) => (
            <button
              key={option}
              onClick={() => handleCategorySelect(option)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition shadow-sm ${
                selectedCategory === option
                  ? "bg-[#283593] text-white border-[#283593]"
                  : "bg-white text-[#283593] border-gray-200 hover:bg-gray-100"
              }`}
            >
              {option}
            </button>
          ))}
        </div>

        <div className="flex items-center">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSend()}
            placeholder={
              !selectedCategory
                ? "Select an option to start..."
                : "Type your message..."
            }
            className={`flex-1 border border-gray-200 rounded-full px-4 py-2.5 text-sm outline-none transition shadow-sm ${
              !selectedCategory || !userEmail
                ? "bg-gray-100 cursor-not-allowed opacity-70"
                : "bg-white focus:border-[#283593] focus:ring-1 focus:ring-[#283593]"
            }`}
            disabled={!userEmail || !selectedCategory}
          />
          <button
            onClick={handleSend}
            className="ml-2 p-3 rounded-full bg-[#283593] text-white shadow hover:bg-[#1e2a78] disabled:bg-gray-300 disabled:shadow-none transition"
            disabled={!userEmail || !input.trim() || !selectedCategory}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VirtualAgent;
